/** Live connection to an online table: one WebSocket per room, reconnecting on its own. */
import { useEffect, useSyncExternalStore } from 'react';
import type { Seat } from '../game/match';
import { PING, type ClientMsg, type RoomView, type RtcSignal, type ServerMsg } from '../shared/room';
import { KEYS, load, save } from './storage';
import { toast } from './toast';

export type ConnStatus = 'connecting' | 'open' | 'reconnecting' | 'gone';

interface Snapshot {
  view: RoomView | null;
  status: ConnStatus;
}

type RtcHandler = (from: Seat, data: RtcSignal) => void;

/** A random per-browser id per table, so a reload (or flaky network) gets your seat back. */
function clientIdFor(code: string): string {
  const ids = load<Record<string, string>>(KEYS.roomClients, {});
  if (!ids[code]) {
    ids[code] = crypto.randomUUID();
    save(KEYS.roomClients, ids);
  }
  return ids[code]!;
}

export function rememberHostKey(code: string, key: string) {
  save(KEYS.roomHosts, { ...load<Record<string, string>>(KEYS.roomHosts, {}), [code]: key });
}

class RoomConnection {
  snap: Snapshot = { view: null, status: 'connecting' };
  private ws: WebSocket | null = null;
  private listeners = new Set<() => void>();
  private rtc = new Set<RtcHandler>();
  private retries = 0;
  private heartbeat = 0;
  private retryTimer = 0;
  private closed = false;

  constructor(readonly code: string) {
    this.connect();
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  onRtc(handler: RtcHandler) {
    this.rtc.add(handler);
    return () => void this.rtc.delete(handler);
  }

  send(msg: ClientMsg) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  dispose() {
    this.closed = true;
    clearInterval(this.heartbeat);
    clearTimeout(this.retryTimer);
    this.ws?.close();
  }

  private set(patch: Partial<Snapshot>) {
    this.snap = { ...this.snap, ...patch };
    this.listeners.forEach((l) => l());
  }

  private connect() {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/api/rooms/${encodeURIComponent(this.code)}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.retries = 0;
      const hostKey = load<Record<string, string>>(KEYS.roomHosts, {})[this.code];
      this.send({ t: 'hello', clientId: clientIdFor(this.code), ...(hostKey ? { hostKey } : {}) });
      clearInterval(this.heartbeat);
      // Mobile networks drop idle sockets; the Worker answers this without waking the table.
      this.heartbeat = window.setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(PING), 25_000);
    };
    ws.onmessage = (e) => {
      if (typeof e.data !== 'string' || !e.data.startsWith('{')) return;
      const msg = JSON.parse(e.data) as ServerMsg;
      if (msg.t === 'state') this.set({ view: msg.view, status: 'open' });
      else if (msg.t === 'rtc') this.rtc.forEach((h) => h(msg.from, msg.data));
      else if (msg.t === 'error') toast(msg.message, 'error');
    };
    ws.onclose = () => {
      clearInterval(this.heartbeat);
      if (this.closed) return;
      this.set({ status: this.snap.view ? 'reconnecting' : 'connecting' });
      const delay = Math.min(10_000, 500 * 2 ** this.retries++);
      this.retryTimer = window.setTimeout(() => this.connect(), delay);
    };
  }
}

const connections = new Map<string, { conn: RoomConnection; users: number }>();

/** Shares one connection per table across components; closes it when the last one unmounts. */
export function useRoom(code: string) {
  const entry = connections.get(code) ?? { conn: new RoomConnection(code), users: 0 };
  connections.set(code, entry);
  useEffect(() => {
    entry.users++;
    return () => {
      entry.users--;
      // Deferred so a remount (StrictMode, quick route change) reuses the live socket.
      setTimeout(() => {
        if (entry.users === 0 && connections.get(code) === entry) {
          entry.conn.dispose();
          connections.delete(code);
        }
      }, 1000);
    };
  }, [entry, code]);
  const snap = useSyncExternalStore(entry.conn.subscribe, () => entry.conn.snap);
  return { ...snap, conn: entry.conn };
}

export type { RoomConnection };
